import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { getClientId } from '../../utils/clientIdentity';

const { FiBarChart2, FiCheck, FiMinimize2, FiPieChart } = FiIcons;

export default function InteractivePollCard() {
  const [activePoll, setActivePoll] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [votes, setVotes] = useState({});
  const [isMinimized, setIsMinimized] = useState(true);

  useEffect(() => {
    fetchActivePoll();
    const channel = supabase
      .channel('public_polls')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gallo_polls_1786733905424' }, fetchActivePoll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gallo_poll_votes_1786733905424' }, fetchActivePoll)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const fetchActivePoll = async () => {
    const { data: pollData } = await supabase
      .from('gallo_polls_1786733905424')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (pollData) {
      setActivePoll(pollData);
      const clientId = getClientId();
      const { data: userVote } = await supabase
        .from('gallo_poll_votes_1786733905424')
        .select('*')
        .eq('poll_id', pollData.id)
        .eq('client_id', clientId)
        .maybeSingle();

      if (userVote) {
        setHasVoted(true);
        setSelectedOption(userVote.option_index);
      } else {
        setHasVoted(false);
        setSelectedOption(null);
      }

      const { data: allVotes } = await supabase
        .from('gallo_poll_votes_1786733905424')
        .select('option_index')
        .eq('poll_id', pollData.id);

      if (allVotes) {
        const counts = {};
        allVotes.forEach(v => { counts[v.option_index] = (counts[v.option_index] || 0) + 1; });
        setVotes(counts);
      }
    } else {
      setActivePoll(null);
    }
  };

  const handleVote = async (index) => {
    if (hasVoted || !activePoll) return;
    setSelectedOption(index);
    const clientId = getClientId();
    const { error } = await supabase
      .from('gallo_poll_votes_1786733905424')
      .insert({ poll_id: activePoll.id, client_id: clientId, option_index: index });
    if (!error) {
      setHasVoted(true);
      fetchActivePoll();
    }
  };

  if (!activePoll) return null;
  const totalVotes = Object.values(votes).reduce((a, b) => a + b, 0);

  if (isMinimized) {
    return (
      <button 
        type="button" 
        onClick={() => setIsMinimized(false)}
        className="flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 shadow-lg transition-all duration-300 transform hover:scale-105"
        title="Ver Encuesta Activa"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
        </span>
        <SafeIcon icon={FiBarChart2} size={16} />
        <span className="text-xs font-bold uppercase tracking-wider">Encuesta Activa</span>
      </button>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/20 dark:border-white/10 bg-white/95 dark:bg-black/80 backdrop-blur-md p-5 space-y-4 shadow-2xl transition-all duration-300 w-full max-w-[320px]"
    >
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/10 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-600/10 text-red-600 dark:text-red-500">
            <SafeIcon icon={FiPieChart} size={14} />
          </span>
          <div>
            <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">Votación en Vivo</h4>
            <p className="text-[9px] text-gray-500">Participa con tu opinión</p>
          </div>
        </div>
        <button 
          type="button" 
          onClick={() => setIsMinimized(true)}
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <SafeIcon icon={FiMinimize2} size={14} />
        </button>
      </div>
      
      <p className="text-sm font-bold text-gray-800 dark:text-gray-100 leading-snug">
        {activePoll.question}
      </p>

      <div className="space-y-2.5">
        <AnimatePresence>
          {activePoll.options.map((opt, idx) => {
            const optVotes = votes[idx] || 0;
            const percent = totalVotes > 0 ? Math.round((optVotes / totalVotes) * 100) : 0;
            const isSelected = selectedOption === idx;

            return (
              <button
                key={idx}
                disabled={hasVoted}
                onClick={() => handleVote(idx)}
                className={`w-full relative overflow-hidden rounded-xl border p-3 text-left transition-all text-xs flex flex-col justify-center ${
                  isSelected 
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-100' 
                    : 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-white/20'
                }`}
              >
                {/* Animated Background Bar Chart */}
                {hasVoted && (
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${percent}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className={`absolute inset-y-0 left-0 opacity-20 ${isSelected ? 'bg-red-600' : 'bg-gray-500 dark:bg-gray-400'}`} 
                  />
                )}
                
                <div className="relative z-10 flex items-center justify-between w-full">
                  <span className="flex items-center gap-2 font-medium">
                    {isSelected && <SafeIcon icon={FiCheck} className="text-red-600 dark:text-red-500" size={14} />}
                    {opt}
                  </span>
                  {hasVoted && (
                    <motion.span 
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                      className={`font-bold ${isSelected ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}
                    >
                      {percent}%
                    </motion.span>
                  )}
                </div>
              </button>
            );
          })}
        </AnimatePresence>
      </div>

      {hasVoted && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] text-gray-500 dark:text-gray-400 text-center font-medium pt-2 border-t border-gray-100 dark:border-white/5">
          {totalVotes} votos registrados en total
        </motion.p>
      )}
    </motion.div>
  );
}